import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '../config';

const photoSrc = (url) => (!url ? '' : (url.startsWith('http') ? url : `${BACKEND_URL}${url}`));
const today = () => new Date().toISOString().slice(0, 10);

const STATUS = {
  attended: { icon: '🟢', label: 'Πήγε', cls: 'bg-emerald-600 text-white' },
  missed: { icon: '🔴', label: 'Δεν πήγε', cls: 'bg-red-600 text-white' },
  pending: { icon: '🟡', label: 'Εκκρεμεί', cls: 'bg-amber-500 text-navy' },
};

const EMPTY_VISIT = { first_name: '', last_name: '', phone: '', visit_date: today(), visit_time: '', comments: '' };

/**
 * Full profile of a collaborator with the visits (referred patients) log.
 * isAdmin=true → can edit collaborator details are handled elsewhere; here admin can delete visits.
 * Both admin and agent can add visits and change status/comments.
 */
const CollaboratorProfileModal = ({ collaborator, isAdmin, onClose, onChanged }) => {
  const [visits, setVisits] = useState([]);
  const [form, setForm] = useState(EMPTY_VISIT);
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState('');
  const [lightbox, setLightbox] = useState(false);
  const c = collaborator;

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/line-collaborators/${c.id}/visits`);
      setVisits(data.visits || []);
    } catch (_e) { /* ignore */ }
  }, [c.id]);

  useEffect(() => { load(); }, [load]);

  const addVisit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!form.first_name.trim() && !form.last_name.trim() && !form.phone.trim()) {
      setErr('Συμπληρώστε τουλάχιστον όνομα ή τηλέφωνο'); return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/admin/line-collaborators/${c.id}/visits`, form);
      setForm({ ...EMPTY_VISIT, visit_date: today() });
      await load();
      onChanged && onChanged();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Σφάλμα καταχώρησης');
    } finally { setAdding(false); }
  };

  const setStatus = async (v, status) => {
    setVisits((prev) => prev.map((x) => x.id === v.id ? { ...x, status } : x));
    try { await axios.patch(`${API}/admin/visits/${v.id}`, { status }); onChanged && onChanged(); }
    catch (_e) { load(); }
  };

  const saveComments = async (v, comments) => {
    if (comments === (v.comments || '')) return;
    try { await axios.patch(`${API}/admin/visits/${v.id}`, { comments }); }
    catch (_e) { load(); }
  };

  const removeVisit = async (v) => {
    if (!window.confirm('Διαγραφή αυτής της επίσκεψης;')) return;
    try { await axios.delete(`${API}/admin/visits/${v.id}`); await load(); onChanged && onChanged(); }
    catch (_e) { /* ignore */ }
  };

  const counts = visits.reduce((a, v) => { a[v.status] = (a[v.status] || 0) + 1; return a; }, {});

  return (
    <div className="fixed inset-0 z-[10001] bg-black/75 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="collab-profile-modal">
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-5xl my-8 rounded-2xl p-5 md:p-6" style={{ background: 'rgba(12,22,40,0.97)', border: '1px solid rgba(201,168,76,0.4)' }}>
        <button onClick={onClose} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-navy border border-gold/50 text-gold flex items-center justify-center hover:bg-gold/10 z-10" data-testid="collab-profile-close" aria-label="Close">✕</button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-2">
        {/* LEFT CARD — doctor info & services */}
        <div className="rounded-xl p-4 bg-navy-200/25 border border-gold/25 self-start" data-testid="collab-info-card">
          <div className="flex flex-col items-center text-center pb-3 border-b border-gold/15">
            {c.photo_url ? (
              <img src={photoSrc(c.photo_url)} alt={c.full_name} onClick={() => setLightbox(true)} className="w-24 h-24 rounded-full object-cover border border-gold/40 cursor-zoom-in hover:ring-2 hover:ring-gold/60 transition" data-testid="collab-profile-avatar" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-navy-200/70 border border-gold/30 flex items-center justify-center text-gold text-3xl font-bold">{(c.full_name || '?').charAt(0).toUpperCase()}</div>
            )}
            <p className="text-gold text-xs font-semibold uppercase tracking-wide mt-3">{c.specialty}</p>
            <h3 className="text-white text-lg font-bold" style={{ fontFamily: 'Georgia, serif' }}>{c.full_name}</h3>
            {(c.gesy_status || c.is_partner) && (
              <div className="flex flex-wrap items-center justify-center gap-2 mt-2" data-testid="collab-badges">
                {c.gesy_status && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-navy-200/60 border border-gold/40 text-gold" data-testid="collab-gesy-badge">
                    <img src="/gesy-badge.png" alt="ΓεΣΥ" className="w-4 h-4 rounded-full object-cover" />
                    {c.gesy_status === 'in' ? 'Εντός ΓεΣΥ' : c.gesy_status === 'out' ? 'Εκτός ΓεΣΥ' : 'Εντός & Εκτός ΓεΣΥ'}
                  </span>
                )}
                {c.is_partner && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-navy-200/60 border border-gold/40 text-slate-200" data-testid="collab-partner-badge" title="Συνεργάτης J.House">
                    <img src="/jhouse-badge.png" alt="J.House" className="w-4 h-4 rounded-full object-contain bg-navy" />
                    Συνεργάτης J.House
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="mt-3 space-y-1.5 text-left">
            {c.city && <p className="text-slate-300 text-sm">📍 {c.city}</p>}
            {c.address && <p className="text-slate-400 text-xs">🏥 {c.address}</p>}
            {c.phones && <p className="text-slate-300 text-xs">📞 {c.phones.split(/[,/]/).map((p, i) => <a key={i} href={`tel:${p.trim()}`} className="text-gold hover:underline mr-2">{p.trim()}</a>)}</p>}
            {c.email && <p className="text-slate-300 text-xs">✉️ <a href={`mailto:${c.email}`} className="text-gold hover:underline">{c.email}</a></p>}
            {c.treatments && (
              <div className="pt-2 mt-2 border-t border-slate-700/50">
                <p className="text-gold/90 text-xs font-semibold mb-1">💊 Υπηρεσίες</p>
                <p className="text-slate-300 text-xs leading-relaxed whitespace-pre-line" data-testid="collab-treatments">{c.treatments}</p>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT CARD — visits management */}
        <div data-testid="collab-visits-card">

        {/* Counters */}
        <div className="flex flex-wrap items-center gap-2 py-3 text-sm">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-900/40 text-emerald-300" data-testid="profile-count-attended">🟢 {counts.attended || 0} Πήγαν</span>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-900/40 text-red-300" data-testid="profile-count-missed">🔴 {counts.missed || 0} Δεν πήγαν</span>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-900/40 text-amber-300" data-testid="profile-count-pending">🟡 {counts.pending || 0} Εκκρεμούν</span>
        </div>

        {/* Add visit */}
        <form onSubmit={addVisit} className="rounded-xl p-3 bg-navy-200/30 border border-slate-700/60 mb-4" data-testid="add-visit-form">
          <p className="text-gold text-sm font-semibold mb-2">Νέα επίσκεψη ασθενή</p>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Όνομα" className="px-3 py-2 rounded-lg bg-navy-200/60 border border-slate-700 text-white text-sm focus:border-gold outline-none" data-testid="visit-first-name" />
            <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Επίθετο" className="px-3 py-2 rounded-lg bg-navy-200/60 border border-slate-700 text-white text-sm focus:border-gold outline-none" data-testid="visit-last-name" />
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Τηλέφωνο" className="px-3 py-2 rounded-lg bg-navy-200/60 border border-slate-700 text-white text-sm focus:border-gold outline-none" data-testid="visit-phone" />
            <input type="date" value={form.visit_date} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} className="px-3 py-2 rounded-lg bg-navy-200/60 border border-slate-700 text-white text-sm focus:border-gold outline-none" data-testid="visit-date" />
            <input type="text" inputMode="numeric" placeholder="Ώρα (π.χ. 14:30)" pattern="[0-9]{1,2}:[0-9]{2}" value={form.visit_time} onChange={(e) => setForm({ ...form, visit_time: e.target.value })} className="px-3 py-2 rounded-lg bg-navy-200/60 border border-slate-700 text-white text-sm focus:border-gold outline-none" data-testid="visit-time" />
          </div>
          <textarea value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} placeholder="Σχόλια / περιγραφή περιστατικού" rows={2} className="w-full px-3 py-2 rounded-lg bg-navy-200/60 border border-slate-700 text-white text-sm focus:border-gold outline-none resize-none mb-2" data-testid="visit-comments" />
          {err && <p className="text-red-400 text-xs mb-2" data-testid="visit-error">{err}</p>}
          <button type="submit" disabled={adding} className="px-4 py-2 rounded-lg font-semibold text-navy text-sm disabled:opacity-60" style={{ background: 'linear-gradient(180deg,#d4b661,#a17f2f)' }} data-testid="add-visit-btn">{adding ? '...' : '+ Καταχώρηση'}</button>
        </form>

        {/* Visits list */}
        <div className="space-y-2 max-h-[320px] overflow-y-auto" data-testid="visits-list">
          {visits.length === 0 && <p className="text-slate-500 text-sm text-center py-4">Καμία επίσκεψη ακόμη.</p>}
          {visits.map((v) => (
            <div key={v.id} className="px-3 py-2.5 rounded-lg bg-navy-200/40 border border-slate-700/60" data-testid={`visit-row-${v.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-medium">{[v.first_name, v.last_name].filter(Boolean).join(' ') || '—'}</p>
                  <p className="text-slate-400 text-xs mt-0.5">
                    {v.phone && <a href={`tel:${v.phone}`} className="text-gold hover:underline mr-3">📞 {v.phone}</a>}
                    {v.visit_date && <span>📅 {v.visit_date}{v.visit_time ? ` ${v.visit_time}` : ''}</span>}
                  </p>
                  {v.confirmation && v.confirmation !== 'pending' ? (
                    <span className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full ${v.confirmation === 'confirmed' ? 'bg-emerald-900/50 text-emerald-300' : 'bg-red-900/50 text-red-300'}`} data-testid={`visit-confirmation-${v.id}`}>
                      {v.confirmation === 'confirmed' ? '✅ Επιβεβαιώθηκε από ασθενή' : '❌ Δεν επιβεβαιώθηκε'}
                    </span>
                  ) : (
                    <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-900/40 text-amber-300" data-testid={`visit-confirmation-${v.id}`}>⏳ Αναμονή επιβεβαίωσης</span>
                  )}
                  {(v.reminder_1day_sent || v.reminder_2h_sent) && (
                    <div className="flex flex-wrap gap-1 mt-1" data-testid={`visit-reminders-${v.id}`}>
                      {v.reminder_1day_sent && <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-900/40 text-sky-300">📩 Υπενθύμιση 1 μέρα ✓</span>}
                      {v.reminder_2h_sent && <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-900/40 text-sky-300">📩 Υπενθύμιση 2 ώρες ✓</span>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {Object.keys(STATUS).map((s) => (
                    <button key={s} type="button" onClick={() => setStatus(v, s)} title={STATUS[s].label}
                      className={`w-8 h-8 rounded-full text-sm flex items-center justify-center transition ${v.status === s ? STATUS[s].cls + ' ring-2 ring-white/40' : 'bg-navy-200/70 opacity-50 hover:opacity-100'}`}
                      data-testid={`visit-status-${s}-${v.id}`}>{STATUS[s].icon}</button>
                  ))}
                  {isAdmin && <button type="button" onClick={() => removeVisit(v)} className="ml-1 text-red-400 hover:text-red-300 text-xs" data-testid={`delete-visit-${v.id}`}>🗑️</button>}
                </div>
              </div>
              <textarea
                defaultValue={v.comments || ''}
                onBlur={(e) => saveComments(v, e.target.value)}
                placeholder="Σχόλια..."
                rows={1}
                className="w-full mt-2 px-2.5 py-1.5 rounded-md bg-navy/40 border border-slate-700/50 text-slate-200 text-xs focus:border-gold outline-none resize-none"
                data-testid={`visit-comments-${v.id}`}
              />
              {v.created_by && <p className="text-[10px] text-slate-500 mt-1">Καταχώρηση: {v.created_by}</p>}
            </div>
          ))}
        </div>
        </div>
        </div>
      </div>

      {lightbox && c.photo_url && (
        <div className="fixed inset-0 z-[10002] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(false)} data-testid="collab-profile-lightbox">
          <img src={photoSrc(c.photo_url)} alt={c.full_name} onClick={(e) => e.stopPropagation()} className="max-w-[92vw] max-h-[85vh] rounded-xl border-2 border-gold/50 object-contain" />
        </div>
      )}
    </div>
  );
};

export default CollaboratorProfileModal;
